import { Pipe, PipeTransform } from '@angular/core';
import { RouteTypes } from '@app/enums/route-types.enum';
import { Learning_Type } from '@app/enums/_index';
import { User } from '@app/models/_index';
import { isValidSubscription } from '@app/utilities/_index';

@Pipe({
  name: 'cardTypeRouteType',
  pure: false,
  standalone: false,
})
export class CardTypeRouteTypePipe implements PipeTransform {
  /**
   * Return transformed learning type string to new display type for unsigned
   * or user that is not logged in
   * NOTE: Pipe must be impure due to the aynchronous nature of the user service which contains the user subscription and enrollments.
   * @param learningType - learning type to transform to new display type
   * @param user - If user is signed in, then the transformed type will return a different display type.
   * @param subscriptionId - Subscription id used to check if the user has this subscription
   * @param courseId - Course id used to check if the user has this enrollment.
   * @param isFreeCourse -  Is Free course if the course IsFreeCourse additional field from course is true, else false.
   * @param sessionId - Session id used to chck if the user has the enrolled session in enrollments.
   * @param countEnroll - Current number of users enrolled in a session.
   * @param maxEnroll - Maximum number of users that can be enrolled in a session.
   * @returns - transformed string indicating if routing is internal, external or modal
   */
  public transform(
    learningType: string,
    user: User,
    subscriptionId: number,
    relatedSubscriptionIds: number[],
    courseId: number,
    isFreeCourse: boolean,
    sessionId: number,
    countEnroll: number,
    maxEnroll: number,
    canEnroll: boolean
  ): string {
    switch (learningType) {
      case Learning_Type.SUBSCRIPTION:
        return RouteTypes.INTERNAL;
      case Learning_Type.E_LEARNING:
      case Learning_Type.QUICK_LEARNING:
      case Learning_Type.WEBINAR:
        if (user) {
          if (user.activeSubscriptions && user.activeSubscriptions.length > 0) {
            // Check if user has active subscription id that matches incoming subscription id
            const isSubscribed =
              subscriptionId &&
              user.activeSubscriptions.find((subscription) => subscription.id === subscriptionId);
            if (isSubscribed) {
              // User is subscribed to this subscription id
              // NOTE: Check if the user enrollment message to update this if they are already in enrolled.
              const enrollment =
                user.enrollments &&
                user.enrollments.length > 0 &&
                user.enrollments.find((courseEnrollment) => courseEnrollment.id === courseId);
              if (enrollment) {
                return RouteTypes.EXTERNAL;
              } else {
                return RouteTypes.INTERNAL;
              }
            } else {
              if (isFreeCourse) {
                // If course is free, then redirect to external
                return RouteTypes.EXTERNAL;
              } else {
                // User is not subscribed to this subscription id, Subscription Required
                return RouteTypes.SUBSCRIPTION;
              }
            }
          } else {
            // User is not subscribed to this subscription id, Subscription Required
            // If the view is detail show learning more, else default to subscription required
            return RouteTypes.INTERNAL;
          }
        } else {
          return RouteTypes.LOGIN;
        }
      case Learning_Type.CERTIFICATION_EXAM:
        if (user) {
          if (user.activeSubscriptions && user.activeSubscriptions.length > 0) {
            // Check if user has active subscription id that matches incoming subscription id
            const isSubscribed =
              subscriptionId &&
              user.activeSubscriptions.find((subscription) => subscription.id === subscriptionId);
            if (isSubscribed) {
              const courseEnrollment = user.enrollments.find(
                (enrollment) => enrollment.id === courseId
              );
              if (courseEnrollment) {
                return RouteTypes.EXTERNAL;
              } else {
                return RouteTypes.EXTERNAL;
              }
            } else {
              // User is not subscribed to this subscription id, then return a modal request a voucher
              return RouteTypes.MODAL;
            }
          } else {
            // User is not subscribed to this subscription id, then return a modal request a voucher
            return RouteTypes.MODAL;
          }
        } else {
          return RouteTypes.LOGIN;
        }
      case Learning_Type.SELF_STUDY:
      case Learning_Type.INSTRUCTOR_LED:
      case Learning_Type.INSTRUCTOR_LED_IN_PERSON:
      case Learning_Type.INSTRUCTOR_LED_VIRTUAL:
      case Learning_Type.VIRTUAL:
      case Learning_Type.IN_PERSON:
      case Learning_Type.WORKSHOP:
        if (user) {
          if (user && user.activeSubscriptions && user.activeSubscriptions.length > 0) {
            // Check if user has active subscription id that matches incoming subscription id
            const isSubscribed = isValidSubscription(relatedSubscriptionIds, subscriptionId, user);
            if (isSubscribed) {
              // Check enrollment sessions and check if the incoming session id
              // NOTE: Check if the user enrollment message to update this if they are already in enrolled.
              const enrollment =
                user.enrollments &&
                user.enrollments.length > 0 &&
                user.enrollments.find((courseEnrollment) => courseEnrollment.id === courseId);
              if (enrollment) {
                const enrolledSession =
                  enrollment.enrolledSessions && enrollment.enrolledSessions.length > 0;
                if (enrolledSession) {
                  // If the user has the enrolled session, then make the route type external
                  // to redirect to docebo. User can be enrolled in a future session that has not
                  // started by they can see the course session info
                  if (enrollment.enrolledSessions.find((session) => session.id === sessionId)) {
                    return RouteTypes.EXTERNAL;
                  } else {
                    return RouteTypes.DISABLED;
                  }
                } else {
                  // Check if the Seats are left for the session to Enroll.
                  if (canEnroll == false) {
                    return RouteTypes.DISABLED;
                  } else if (maxEnroll > countEnroll) {
                    return RouteTypes.MODAL; // Seats still left for the session to enroll
                    // } else {
                    // BEYOND-2757: Disable this until is ILT 5 Day Policy issues have been resolved.
                    //  return RouteTypes.DISABLED;
                  }
                }
              } else {
                // Check if the Seats are left for the session to Enroll.
                if (canEnroll == false) {
                  return RouteTypes.DISABLED;
                } else if (maxEnroll > countEnroll) {
                  return RouteTypes.MODAL; // Seats still left for the session to enroll
                } else {
                  return RouteTypes.DISABLED;
                }
              }
            } else {
              if (isFreeCourse) {
                // If course is free, then redirect to external
                return RouteTypes.EXTERNAL;
              } else {
                // User is not subscribed to this subscription id
                return RouteTypes.INTERNAL;
              }
            }
          } else {
            // User is not subscribed to this subscription id
            return RouteTypes.INTERNAL;
          }
        } else {
          return RouteTypes.LOGIN;
        }
      default:
        return RouteTypes.INTERNAL;
    }
  }
}
